/**
 * detection-odor-environment.js
 */

import React from 'react';
import {
  Grid,
  Typography,
} from '@mui/material';
import { useFieldArray, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import get from 'lodash.get';
import { Types } from 'utils/constants';
import { sleep } from 'utils/misc';
import HelpTextItem from 'components/menu-drawer/help-text-item';
import EditorAccordion from 'components/menu-drawer/editor-accordion';
import HiddenInput from 'components/form-inputs/hidden-input';
import TextField from 'components/form-inputs/text-field';
import Select from 'components/form-inputs/select';
import { DetectionOdorsContext } from '../context/detection-odors-context';
import DetectionExpander from './detection-expander';
import { OdorExpander } from './detection-odor';
import { 
  defaultOdor, 
  sectionTypes, 
  getOdorIds, 
  odorlessEnvironmentExists, 
  updateDefaultToCurrent 
} from './detection-common';


export function OdorEnvironmentSection(props) {
  const {
    baseName,
    recordType,
    odorEnvIndex,
    envIndex,
    odorEnvironment,
    environment,
    envTypeId,
    onDelete,
    options,
    formContext,
    addingFormContext,
    disabled,
    drawerState,
    translation
  } = props;
  const { setExpandedId } = React.useContext(DetectionOdorsContext);
  const { control, getValues, setValue } = formContext;
  const { fields: odors, append, remove } = useFieldArray({
    control,
    name: `${baseName}[${envIndex}].odorEnvironments[${odorEnvIndex}].odors`,
    keyName: 'hookFormId'
  });


  // add odor
  const doAdd = async () => {
    drawerState.loading.content.set(true);

    const newOdor = get(addingFormContext.getValues(), baseName)[envIndex]
      .odorEnvironments[odorEnvIndex]
      .odors[0];

    setExpandedId();
    await sleep(0);
    append(newOdor);
    await sleep(100);

    // update cached list
    setValue('detectionOdorIds', getOdorIds(get(getValues(), baseName)));
    setValue('detectionOdorlessEnvironmentExists', odorlessEnvironmentExists(get(getValues(), baseName)));
    
    // update default values with newly-added odor. 
    const formNamePrefix = `${baseName}[${envIndex}].odorEnvironments[${odorEnvIndex}].odors[${odors?.length ?? 0}]`;
    updateDefaultToCurrent(formContext, formNamePrefix, newOdor);

    drawerState.loading.content.set(false);
  }


  // remove odor
  const doRemove = (index) => {
    remove(index);

    // update cached list after remove is complete
    setTimeout(() => {
      setValue('detectionOdorIds', getOdorIds(get(getValues(), baseName)), { shouldDirty: true });
      setValue('detectionOdorlessEnvironmentExists', odorlessEnvironmentExists(get(getValues(), baseName)), { shouldDirty: true });
    });
  }


  return (
    <>
      <OdorEnvironmentExpander
        id={odorEnvironment.hookFormId}
        recordType={recordType}
        baseName={baseName}
        isNew={false}
        odorEnvIndex={odorEnvIndex}
        envIndex={envIndex}
        odorEnvironment={odorEnvironment}
        environment={environment}
        envTypeId={envTypeId}
        onDelete={onDelete}
        options={options}
        formContext={formContext}
        disabled={disabled}
        drawerState={drawerState}
        translation={translation}
      />
      {odors && odors.map((odor, odorIndex) =>
        <OdorExpander
          key={odor.hookFormId}
          id={odor.hookFormId}
          recordType={recordType}
          baseName={baseName}
          isNew={false}
          odorIndex={odorIndex}
          odorEnvIndex={odorEnvIndex}
          envIndex={envIndex}
          odor={odor}
          onDelete={(index) => doRemove(index)}
          options={options}
          formContext={formContext}
          disabled={disabled}
          drawerState={drawerState}
          translation={translation}
        />)}
      <OdorExpander
        id={`${odorEnvironment.hookFormId}-new-odor`}
        recordType={recordType}
        baseName={baseName}
        isNew={true}
        odorIndex={0}
        odorEnvIndex={odorEnvIndex}
        envIndex={envIndex}
        odor={defaultOdor(options, recordType)}
        onAdd={doAdd}
        options={options}
        formContext={addingFormContext}
        disabled={disabled}
        drawerState={drawerState}
        translation={translation}
      />
    </>
  );
}


export function OdorEnvironmentExpander(props) {
  const {
    id,
    recordType,
    baseName,
    isNew,
    odorEnvIndex,
    envIndex,
    odorEnvironment,
    envTypeId,
    onAdd,
    onDelete,
    options,
    formContext,
    disabled,
    drawerState,
    translation
  } = props;
  const formNamePrefix = `${baseName}[${envIndex}].odorEnvironments[${odorEnvIndex}]`;
  const { t: gt } = useTranslation('general');
  const { t } = useTranslation(translation);
  const { control, formState, getValues, setValue, trigger, resetField } = formContext;
  const odorEnvName = useWatch({ control, name: `${formNamePrefix}.name` });
  const vehicleMake = useWatch({ control, name: `${formNamePrefix}.vehicle.make` });
  const vehicleModel = useWatch({ control, name: `${formNamePrefix}.vehicle.model` });
  const errorBase = get(formState.errors, baseName)?.[envIndex]?.odorEnvironments?.[odorEnvIndex];
  const [selectedCountry, setSelectedCountry] = React.useState();
  const vehicleCountryId = odorEnvironment?.vehicle?.countryId;
  const vehicleRegionId = odorEnvironment?.vehicle?.regionId;
  const title = recordType === Types.Record.DEPLOYMENT && envTypeId === Types.Environment.VEHICLE
    ? `${vehicleMake} ${vehicleModel}`
    : (odorEnvName ?? '');
  const environmentName = gt(`types.environment.${envTypeId}`);
  const objectSuffix = recordType === Types.Record.EXERCISE ? 'With Odor' : 'Indication';
  const subTitle = `${environmentName} ${objectSuffix}`;


  // deployment vehicle - country select changed
  const onCountryChanged = React.useCallback((countryId) => {
    const country = options.countries.find(c => c.id === countryId);
    setSelectedCountry(country);

    // update selected region
    const isRegionValid = Boolean(vehicleRegionId && country.regions.find(r => r.id === vehicleRegionId));
    setValue(`${formNamePrefix}.vehicle.regionId`, isRegionValid ? vehicleRegionId : '');
  }, [options, formNamePrefix, setValue, vehicleRegionId]);


  // resets to given values or last-saved values if given values is null
  const resetToValues = (values) => {
    resetField(`${formNamePrefix}.name`, { defaultValue: values?.name ?? undefined, keepDirty: true });
    resetField(`${formNamePrefix}.description`, { defaultValue: values?.description ?? undefined, keepDirty: true });

    if (envTypeId === Types.Environment.VEHICLE) {
      resetField(`${formNamePrefix}.vehicle.type`, { defaultValue: values?.vehicle?.type ?? undefined, keepDirty: true });
      resetField(`${formNamePrefix}.vehicle.color`, { defaultValue: values?.vehicle?.color ?? undefined, keepDirty: true });
      resetField(`${formNamePrefix}.vehicle.make`, { defaultValue: values?.vehicle?.make ?? undefined, keepDirty: true });
      resetField(`${formNamePrefix}.vehicle.model`, { defaultValue: values?.vehicle?.model ?? undefined, keepDirty: true });
      resetField(`${formNamePrefix}.vehicle.description`, { defaultValue: values?.vehicle?.description ?? undefined, keepDirty: true });

      if (recordType === Types.Record.DEPLOYMENT) {
        resetField(`${formNamePrefix}.vehicle.countryId`, { defaultValue: values?.vehicle?.countryId ?? undefined, keepDirty: true });
        const countryId = getValues(`${formNamePrefix}.vehicle.countryId`);
        onCountryChanged(countryId);
        resetField(`${formNamePrefix}.vehicle.regionId`, { defaultValue: values?.vehicle?.regionId ?? undefined, keepDirty: true });
      }
    }
  }


  // deployment vehicle - init country options
  React.useEffect(() => {
    if (recordType === Types.Record.DEPLOYMENT && envTypeId === Types.Environment.VEHICLE) {
      if (vehicleCountryId) {
        onCountryChanged(vehicleCountryId);
      } else if (options.recentCommonValues?.location?.country?.id) {
        onCountryChanged(options.recentCommonValues?.location?.country?.id);
      }
    }
  }, [
    recordType, 
    envTypeId, 
    vehicleCountryId, 
    vehicleRegionId,
    formNamePrefix, 
    options.recentCommonValues, 
    setValue,
    onCountryChanged
  ]);


  const onDone = async () => {
    // validate
    let validationList = [];

    if (recordType === Types.Record.DEPLOYMENT && envTypeId === Types.Environment.VEHICLE) {
      validationList.push(`${formNamePrefix}.vehicle.make`);
      validationList.push(`${formNamePrefix}.vehicle.model`);
      validationList.push(`${formNamePrefix}.vehicle.licensePlate`);
    } else {
      validationList.push(`${formNamePrefix}.name`);
    }

    const isValid = await trigger(validationList);

    if (isValid) {
      if (isNew) {
        await onAdd();
        resetToValues(odorEnvironment);
      } else {
        updateDefaultToCurrent(formContext, formNamePrefix, getValues(formNamePrefix));
      }

      return true;
    } 
    
    return false;
  }


  const onCancel = () => {
    resetToValues(isNew ? odorEnvironment : null);
  }


  return (
    <DetectionExpander
      id={id}
      type={sectionTypes.ODOR_ENVIRONMENT}
      typeTitle={subTitle}
      title={title}
      iconName='odor-environment'
      isNew={isNew}
      onDone={onDone}
      onCancel={onCancel}
      onDelete={() => onDelete(odorEnvIndex)}
      disabled={disabled}
      data-testtype='odor-environment-expander'
      error={Boolean(
        errorBase?.name
        || errorBase?.vehicle?.make
        || errorBase?.vehicle?.model
      )}
    >
      <Grid 
        container
        rowSpacing={0}
        columnSpacing={2}
      >

        {/* odor environment id - hidden */}
        <HiddenInput
          formContext={formContext}
          name={`${formNamePrefix}.id`}
          defaultValue={odorEnvironment.id}
          setValueAs={(value) => value ? parseInt(value) : null}
        />
        

        {/* odor environment name */}
        {(recordType !== Types.Record.DEPLOYMENT || envTypeId !== Types.Environment.VEHICLE) &&
        <Grid item xs={12} md={6}>
          <HelpTextItem
            sidebarText={drawerState.sidebarText}
            title={t('environments.environmentName.helpTitle')}
            text={t('environments.environmentName.helpInfo')}
            minimalHoverStyle
          >
            {focusHandlers => 
              <TextField
                formContext={formContext}
                name={`${formNamePrefix}.name`}
                label={`${environmentName} ${t('environments.environmentName.label')}`}
                defaultValue={odorEnvironment.name}
                focusHandlers={focusHandlers}
                rules={{
                  required: t('environments.environmentName.errMsgEmpty'),
                  validate: value => value.trim() !== '' || t('environments.environmentName.errMsgEmpty'),
                  maxLength: {
                    value: 500,
                    message: t('environments.environmentName.errMsgLength')
                  }
                }}
              />}
          </HelpTextItem>
        </Grid>}
      

        <Grid item xs={12} style={{ padding: 0 }} />


        {/* DEPLOYMENT-ONLY - vehicle make and model (required) */}
        {recordType === Types.Record.DEPLOYMENT && envTypeId === Types.Environment.VEHICLE &&
        <>

          {/* vehicle make */}
          <Grid item xs={12} sm={6} xl={6}>
            <HelpTextItem
              sidebarText={drawerState.sidebarText}
              title={t('environments.vehicleMake.helpTitle')}
              text={t('environments.vehicleMake.helpInfo')}
              minimalHoverStyle
            >
              {focusHandlers => 
                <TextField
                  formContext={formContext}
                  name={`${formNamePrefix}.vehicle.make`}
                  label={t('environments.vehicleMake.label')}
                  defaultValue={odorEnvironment.vehicle?.make || ''}
                  focusHandlers={focusHandlers}
                  rules={{
                    required: t('environments.vehicleMake.errMsgEmpty'),
                    validate: value => value.trim() !== '' || t('environments.vehicleMake.errMsgEmpty'),
                    maxLength: {
                      value: 50,
                      message: t('environments.vehicleMake.errMsgLength')
                    }
                  }}
                />}
            </HelpTextItem>
          </Grid>


          {/* vehicle model */}
          <Grid item xs={12} sm={6} xl={6}>
            <HelpTextItem
              sidebarText={drawerState.sidebarText}
              title={t('environments.vehicleModel.helpTitle')}
              text={t('environments.vehicleModel.helpInfo')}
              minimalHoverStyle
            >
              {focusHandlers => 
                <TextField
                  formContext={formContext}
                  name={`${formNamePrefix}.vehicle.model`}
                  label={t('environments.vehicleModel.label')}
                  defaultValue={odorEnvironment.vehicle?.model || ''}
                  focusHandlers={focusHandlers}
                  rules={{
                    required: t('environments.vehicleModel.errMsgEmpty'),
                    validate: value => value.trim() !== '' || t('environments.vehicleModel.errMsgEmpty'),
                    maxLength: {
                      value: 50,
                      message: t('environments.vehicleModel.errMsgLength')
                    }
                  }}
                />}
            </HelpTextItem>
          </Grid>

        </>}



        {/* OPTIONAL */}
        <Grid item xs={12}>
          <EditorAccordion
            highlight
            name="With Odor Optional"
            data-testid={`odor-environment-optional-accordion-${odorEnvIndex}`}
            summary={<>
              <Typography variant="body2">
                {window.innerWidth < 450 
                  ? 'Optional Fields' 
                  : `OPTIONAL: Description${envTypeId === Types.Environment.VEHICLE ? ', Vehicle Details' : ''}`}
              </Typography>
            </>}
            detail={
              <>
                <Grid
                  container
                  rowSpacing={0}
                  columnSpacing={2}
                >

                  {envTypeId === Types.Environment.VEHICLE &&
                  <>

                    {/* vehicle type */}
                    <Grid item xs={12} sm={6} xl={6}>
                      <Select
                        formContext={formContext}
                        name={`${formNamePrefix}.vehicle.type`}
                        label={t('environments.vehicleType.label')}
                        options={options.vehicleTypes}
                        style={{ width: '100%', margin: '12px 0' }}
                        defaultValue={odorEnvironment.vehicle?.type || ''}
                        setValueAs={id => ({ id })}
                      />
                    </Grid>


                    {/* vehicle color */}
                    <Grid item xs={12} sm={6} xl={6}>
                      <TextField
                        formContext={formContext}
                        name={`${formNamePrefix}.vehicle.color`}
                        label={t('environments.vehicleColor.label')}
                        style={{ width: '100%', margin: '12px 0' }}
                        defaultValue={odorEnvironment.vehicle?.color || ''}
                      />
                    </Grid>

                    
                    {/* vehicle make */}
                    {recordType === Types.Record.EXERCISE &&
                    <Grid item xs={12} sm={6} xl={6}>
                      <TextField
                        formContext={formContext}
                        name={`${formNamePrefix}.vehicle.make`}
                        label={t('environments.vehicleMake.label')}
                        style={{ width: '100%', margin: '12px 0' }}
                        defaultValue={odorEnvironment.vehicle?.make || ''}
                      />
                    </Grid>}


                    {/* vehicle model */}
                    {recordType === Types.Record.EXERCISE &&
                    <Grid item xs={12} sm={6} xl={6}>
                      <TextField
                        formContext={formContext}
                        name={`${formNamePrefix}.vehicle.model`}
                        label={t('environments.vehicleModel.label')}
                        style={{ width: '100%', margin: '12px 0' }}
                        defaultValue={odorEnvironment.vehicle?.model || ''}
                      />
                    </Grid>}


                    {/* vehicle license country */}
                    <Grid item xs={12} sm={6} xl={6}>
                      <Select
                        formContext={formContext}
                        name={`${formNamePrefix}.vehicle.countryId`}
                        label={'Country'}
                        options={options.countries}
                        defaultValue={
                          odorEnvironment.vehicle?.countryId 
                          || options.recentCommonValues?.location?.country?.id
                          || ''}
                        style={{ width: '100%', margin: '12px 0 8px 0' }}
                        onChange={e => onCountryChanged(e.target.value)}
                      />
                    </Grid>


                    {/* vehicle license region */}
                    {selectedCountry &&
                    <Grid item xs={12} sm={6} xl={6}>
                      <Select
                        formContext={formContext}
                        name={`${formNamePrefix}.vehicle.regionId`}
                        label={`${selectedCountry.regionTerm}`}
                        options={selectedCountry.regions}
                        defaultValue={
                          odorEnvironment.vehicle?.regionId
                          || options.recentCommonValues?.location?.region?.id
                          || ''
                        }
                        style={{ width: '100%', margin: '12px 0 8px 0' }}
                      />
                    </Grid>}

                    
                    {/* license plate - deployment only */}
                    {recordType === Types.Record.DEPLOYMENT &&
                    <Grid item xs={12} sm={6} xl={6}>
                      <TextField
                        formContext={formContext}
                        name={`${formNamePrefix}.vehicle.licensePlate`}
                        label={t('environments.vehicleLicensePlate.label')}
                        defaultValue={odorEnvironment.vehicle?.licensePlate || ''}
                        style={{ width: '100%', margin: '12px 0 8px 0' }}
                        rules={{
                          maxLength: {
                            value: 20,
                            message: t('environments.vehicleLicensePlate.errMsgLength')
                          }
                        }}                        
                      />
                    </Grid>}

                  </>}


                  {/* description */}
                  {(recordType !== Types.Record.DEPLOYMENT || envTypeId !== Types.Environment.VEHICLE) &&
                  <Grid item xs={12} style={{ marginBottom: 8 }}>
                    <HelpTextItem
                      sidebarText={drawerState.sidebarText}
                      title={t('environments.environmentDescription.helpTitle')}
                      text={t('environments.environmentDescription.helpInfo')}
                      minimalHoverStyle
                    >
                      {focusHandlers =>
                        <TextField
                          formContext={formContext}
                          name={`${formNamePrefix}.description`}
                          label={`${environmentName} ${t('environments.environmentDescription.label')}`}
                          defaultValue={odorEnvironment.description}
                          focusHandlers={focusHandlers}
                          style={{ width: '100%' }}
                          multiline
                          rows={2}
                        />}
                    </HelpTextItem>
                  </Grid>}

                </Grid>
              </>
            }
            />
        </Grid>

      </Grid>
    </DetectionExpander>
  );
}